import { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useUserContext } from 'layers/content/v2/hooks/useUserContext'
import { useCurriculumCompletionContext } from './curriculumCompletionContext'
import { operations as curriculumCourseOperations } from 'store/curriculumCourseManager'
import { operations as lmsOperations } from 'store/lmsManager'
import { findNode } from 'utils/highschoolHelpers'
import { resolveK12TrainingProgramFromUrl } from 'utils/program/training'
import { findLeafNodeIdFromLessonNodes } from 'utils/lms/lessonNodeHelpers'
import {
  getLessonCompletionState,
  resolveIndexBasedLeafNodeId,
} from './courseTreeHelpers'
import {
  buildTrainingUserContextB64,
  isTrainingMarkCompleteContext,
  resolveRegistrationId,
  usesK8ProgramTrainingAttempt,
} from './registrationHelpers'

/**
 * Encapsulates curriculum-service based mark-complete behavior for
 * IndividualResourceV3 routes.
 */
export function useMarkCompleteV3({
  coursePointer,
  courseNodeId: rawCourseNodeId,
  contentGroupCode,
  instructorDataKey,
  siteId: explicitSiteId = null,
  lessonSlug = null,
}) {
  const dispatch = useDispatch()
  const userContext = useUserContext()
  const courseNodeId = rawCourseNodeId || null

  // Use shared context for common data fetching and resolution
  const {
    resolvedSiteId: siteId,
    classId,
    instructorData,
    isFetchingOrCreating,
    hasAttemptedInstructorFetch,
    registrations,
    courseCodeForTree,
    coursePointerForTreeFetch,
    courseTreeEntry,
    courseTreeData,
    transformedCourseTree,
  } = useCurriculumCompletionContext({
    instructorDataKey,
    coursePointer,
    contentGroupCode,
    explicitSiteId,
  })

  // --- Course tree node finding (mark-complete specific) ---

  const courseTreeNode = useMemo(() => {
    if (!transformedCourseTree || !courseNodeId) return null
    return findNode(transformedCourseTree, courseNodeId)
  }, [transformedCourseTree, courseNodeId])

  const lessonType = courseTreeNode?.type || ''

  const treeLeafNodeId = useMemo(() => {
    if (!courseTreeNode) {
      if (courseTreeEntry?.data && courseNodeId) {
        console.error(
          `[useMarkCompleteV3] courseNodeId "${courseNodeId}" not found in course tree (contentGroupCode: "${contentGroupCode}")`,
        )
      }
      return null
    }
    return courseTreeNode.leafNodeId || null
  }, [courseTreeEntry?.data, courseTreeNode, courseNodeId, contentGroupCode])

  const registrationId = useMemo(
    () =>
      resolveRegistrationId({
        instructorData,
        registrations,
        classId,
        courseCode: courseCodeForTree,
        instructorDataKey,
        lessonType,
      }),
    [
      instructorData,
      registrations,
      classId,
      courseCodeForTree,
      instructorDataKey,
      lessonType,
    ],
  )

  const fallbackLeafNodeId = useMemo(() => {
    if (!courseNodeId) return null
    if (!registrations.length) return null
    const matchedRegistration =
      registrationId != null
        ? registrations.find(
            reg => String(reg.registration_id) === String(registrationId),
          )
        : null

    if (matchedRegistration) {
      return findLeafNodeIdFromLessonNodes(
        matchedRegistration.lesson_nodes,
        courseNodeId,
      )
    }

    for (const registration of registrations) {
      const foundLeafNodeId = findLeafNodeIdFromLessonNodes(
        registration.lesson_nodes,
        courseNodeId,
      )
      if (foundLeafNodeId) return foundLeafNodeId
    }
    return null
  }, [registrations, registrationId, courseNodeId])

  const indexBasedLeafNodeId = useMemo(() => {
    if (contentGroupCode !== 'dwb' || !lessonSlug) return null

    return resolveIndexBasedLeafNodeId({
      courseTreeData,
      lessonSlug,
    })
  }, [contentGroupCode, courseTreeData, lessonSlug])

  const leafNodeId =
    treeLeafNodeId || fallbackLeafNodeId || indexBasedLeafNodeId

  const { isComplete, completedAt } = useMemo(
    () =>
      getLessonCompletionState({
        registrations,
        registrationId,
        courseNodeId,
        leafNodeId,
      }),
    [registrations, registrationId, courseNodeId, leafNodeId],
  )

  const handleMarkComplete = async () => {
    if (isComplete) return false

    const isTrainingContext = isTrainingMarkCompleteContext(
      lessonType,
      courseCodeForTree,
    )
    const usesK8TrainingAttempt = usesK8ProgramTrainingAttempt(
      lessonType,
      courseCodeForTree,
    )
    const resolvedLeafNodeId = isTrainingContext
      ? leafNodeId || courseNodeId
      : leafNodeId
    const isInstructorDataPending =
      isFetchingOrCreating || (!instructorData && !hasAttemptedInstructorFetch)

    if (!registrationId || !resolvedLeafNodeId) {
      if (isInstructorDataPending || courseTreeEntry?.loading) {
        return false
      }

      if (
        registrationId &&
        !leafNodeId &&
        coursePointerForTreeFetch &&
        !courseTreeEntry?.loading
      ) {
        await dispatch(
          curriculumCourseOperations.fetchCourseTreeFromPointer({
            coursePointer: coursePointerForTreeFetch,
            contentGroupCode,
            siteId,
          }),
        )
      }

      const missingFields = [
        !registrationId ? 'registrationId' : null,
        !resolvedLeafNodeId ? 'leafNodeId' : null,
      ].filter(Boolean)

      console.error(
        `[useMarkCompleteV3] ${missingFields.join(
          ' and ',
        )} required to mark complete`,
        {
          contentGroupCode,
          courseCodeForTree,
          courseNodeId,
          instructorDataKey,
          lessonType,
          isTrainingContext,
        },
      )
      return false
    }

    try {
      if (usesK8TrainingAttempt) {
        const userContextB64 = buildTrainingUserContextB64(
          userContext,
          resolveK12TrainingProgramFromUrl(),
        )

        await dispatch(
          lmsOperations.makeAttemptV2({
            registrationId,
            leafNodeId: resolvedLeafNodeId,
            progress: 1,
            userContextB64,
          }),
        )
      } else {
        await dispatch(
          lmsOperations.makeAttemptV2Direct({
            registrationId,
            leafNodeId: resolvedLeafNodeId,
            progress: 1,
          }),
        )
      }

      await dispatch(
        lmsOperations.refreshInstructorProgramFlexAfterAttempt(userContext),
      )
      return true
    } catch (error) {
      console.error('[useMarkCompleteV3] failed to mark complete', error)
      return false
    }
  }

  return {
    isComplete,
    completedAt,
    handleMarkComplete,
    lessonType,
    registrationId,
    leafNodeId,
  }
}

export { useCurriculumCompletionContext } from './curriculumCompletionContext'
export { useUnitLessonCompletion } from './unitLessonCompletion'
